import React, { useMemo, useState, useEffect, useCallback, Fragment } from 'react';
import DatePicker from '../../components/DatePicker';
import SelectBox from '../../components/SelectBox';
import { resultHubFilters } from '../../utils/resultHubDemoData';
import ResultCard from './components/ResultCard';
import goalResultService from '../../services/goalResultService';
import moment from 'moment/moment';
import { LoadingSpinner } from '../../components';
import SnapshotModal from './components/SnapshotModal';
import CustomSearchBtn from '../../components/CustomSearchBtn';
import CustomNoData from '../../components/CustomNoData';
import { useWindowWidth } from '@react-hook/window-size';
import { toast } from 'react-toastify';

const STATUS_MAP = {
  '0': { label: 'Not Start', id: 'upcoming' },
  '1': { label: 'InPlay', id: 'ongoing' },
  '3': { label: 'Ended', id: 'finished' },
  '4': { label: 'Postponed', id: 'postpond' },
  '5': { label: 'Postponed', id: 'postpond' },
  '6': { label: 'Postponed', id: 'postpond' },
  '7': { label: 'Postponed', id: 'postpond' },
  '8': { label: 'Postponed', id: 'postpond' },
};

const getStatusMeta = (status) => STATUS_MAP[status] ?? { label: '', id: null };

const formatScore = (home, away) => {
  const isScoreAvailable = home !== null && home !== undefined && away !== null && away !== undefined;
  return isScoreAvailable ? `${home} vs ${away}` : '-';
};

const buildMatchup = (home, away) => {
  const safeHome = home ?? '-';
  const safeAway = away ?? '-';
  return `${safeHome} vs ${safeAway}`;
};

const createLeagueId = (leagueName = '') => {
  const slug = leagueName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return slug.length > 0 ? slug : 'unknown-league';
};

const ResultHub = () => {
  const onlyWidth = useWindowWidth();
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [resultHubData, setResultHubData] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(resultHubFilters.leagues[0]);
  const [selectedStatus, setSelectedStatus] = useState(resultHubFilters.statuses[0]);
  const [selectedSnapshot, setSelectedSnapshot] = useState({
    snapshots: [], league: '', kickoff: '', fixtures: []
  });
  const [counting,setCounting] = useState({
    allTotal : 0,
    matchingTotal : 0
  });

  const fetchResultHub = async () => {
    try {
      setIsLoading(true);
      const response = await goalResultService.getResultHub(selectedDate);
      

      if (response.status === 200) {
        const formattedData = (response.data?.data ?? []).sort((a, b) => new Date(a.baseEventTime?.value) - new Date(b.baseEventTime?.value)).map((item) => {
          const leagueName = item.baseLeagueName ?? 'Unknown League';
          const leagueId = createLeagueId(leagueName);
          const baseStatus = getStatusMeta(item.baseStatus);
          const refStatus = getStatusMeta(item.refStatus);

          return {
            id: String(item.combination_id ?? `${item.baseEventId}-${item.refEventId}`),
            league: leagueName,
            leagueId,
            kickoff: item.baseEventTime ? moment(item.baseEventTime.value).format("YYYY-MM-DD hh:mm A") : '-',
            fixtures: [
              {
                id: String(item.baseEventId ?? null),
                bookmaker: 'Sport909',
                matchup: buildMatchup(item.baseHomeTeam, item.baseAwayTeam),
                status: baseStatus.label,
                statusId: baseStatus.id,
                score: formatScore(item.baseHomeGoal, item.baseAwayGoal),
              },
              {
                id: String(item.refEventId ?? null),
                bookmaker: '555Mix',
                matchup: buildMatchup(item.refHomeTeam, item.refAwayTeam),
                status: refStatus.label,
                statusId: refStatus.id,
                score: formatScore(item.refHomeGoal, item.refAwayGoal),
              },
            ],
            snapshotDetails: item.snapshotDetails ?? [],
          };
        });

        console.log('Fetched Result Hub Data:', formattedData.filter(a => a.snapshotDetails.length > 0));

        const formattedLeague = [
          { id: "all", name: "All Leagues" },
          ...new Map(
            formattedData.map(v => [v.leagueId, { id: v.leagueId, name: v.league }])
          ).values()
        ];

        const tempCounting = {
          allTotal : formattedData.length,
          matchingTotal : formattedData.filter(a => a.fixtures[1].id !== "null").length
        }

        setLeagues(formattedLeague);
        setCounting(tempCounting);
        setSelectedLeague(formattedLeague[0]);
        setResultHubData(formattedData);
      } else {
        setResultHubData([]);
      }
    } catch (error) {
      console.error('Failed to fetch result hub data', error);
      setResultHubData([]);
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResultHub();
  }, []);

  const filteredMatches = useMemo(() => {
    const leagueId = selectedLeague.id;
    const statusId = selectedStatus.id;
    const keyword = searchTerm.trim().toLowerCase();

    return resultHubData
      .filter((match) => {
        if (leagueId === 'all') {
          return true;
        }

        const matchLeagueId = match.leagueId ?? match.id;
        return matchLeagueId === leagueId;
      })
      .map((match) => {
        const fixtures = Array.isArray(match.fixtures) ? match.fixtures : [];

        return {
          ...match,
          fixtures: fixtures.filter((fixture) => {
            const matchStatus = statusId === 'all' || statusId === 'snapshot' ? true : fixture.statusId === statusId;
            console.log('Filtering fixture:', fixture, 'Match Status:', matchStatus);
            const lowercaseMatchup = fixture.matchup?.toLowerCase?.() ?? '';
            const matchKeyword = keyword.length === 0 ? true : lowercaseMatchup.includes(keyword);
            return matchStatus && matchKeyword;
          }),
        };
      })
      .filter((match) => statusId === 'snapshot' ? match.snapshotDetails.length > 0 : true)
      .filter((match) => match.fixtures.length > 0);
  }, [selectedLeague, selectedStatus, searchTerm, resultHubData]);

  const handleOpen = async (snapshots,league,kickoff,fixtures) => {
    setSelectedSnapshot({snapshots,league,kickoff,fixtures});
    setOpen(true);
  }

  const handleSearch = async () => {
    fetchResultHub();
  }

  const SaveResult = async (payload) => {
    try {
      setSaveLoading(true);
      const response = await goalResultService.saveResultHub(payload);

      if (response.status === 200) {
        if (response.data?.data.matchResultUpdate === true && response.data?.data.snapshotUpdate === true) {
          toast.success('Result saved successfully');
          setOpen(false);
          await fetchResultHub();
        } else {
          toast.error('No changes were made to save');
        }
      }
    }
    catch (error) {
      console.error('Failed to fetch result hub data', error);
    }
    finally {
      setSaveLoading(false);
    }
  }

  return (
    <Fragment>
      <SnapshotModal
        selectedSnapshot={selectedSnapshot}
        open={open}
        setOpen={setOpen}
        isMobile={onlyWidth < 768}
        saveLoading={saveLoading}
        onSave={SaveResult}
      />
      <div className="min-h-screen w-full bg-customGray dark:bg-custom_dark_background px-3 py-2">
        <div className='flex justify-between items-center'>
          <span className='text-base text-background dark:text-customBackColor font-semibold'>Result Hub</span>
          <div className='flex text-xs divide-x divide-customBlack/80 dark:divide-customMuted text-background dark:text-customBackColor'>
            <span className='px-1'>Total - {counting?.allTotal}</span>
            <span className='px-1'>Matching - {counting?.matchingTotal}</span>
          </div>
        </div>
        <div className="mx-auto flex flex-col gap-2">
          <div className="rounded-lg bg-white dark:bg-custom_dark_primary2 dark:border-custom_dark_secondary p-2 shadow-lg">
            <div className="grid grid-cols-1 gap-2 md:gap-3 md:grid-cols-6 items-center">
              <div className="md:col-span-1">
                <DatePicker date={selectedDate} setDate={setSelectedDate} isMobile={onlyWidth < 768}/>
              </div>
              <div className="md:col-span-1">
              <CustomSearchBtn handleSearch={handleSearch} extraStyle={"mt-2"}/>
              </div>
              <div className="md:col-span-2">
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-customMuted">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 4.245 12.012l4.246 4.246a.75.75 0 1 0 1.06-1.06l-4.246-4.247A6.75 6.75 0 0 0 10.5 3.75Zm-5.25 6.75a5.25 5.25 0 1 1 10.5 0 5.25 5.25 0 0 1-10.5 0Z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search teams..."
                    className={`${onlyWidth < 768 ? 'text-xs' : 'text-sm'} w-full rounded-lg border border-customLightMilkWhite dark:border-custom_dark_secondary bg-customBlack/5 py-2 pl-11 pr-4 font-medium text-customBlack dark:text-customLightMilkWhite shadow-sm focus:outline-none focus:ring-2 focus:ring-customLightMilkWhite`}
                  />
                </div>
              </div>
              <div className="md:col-span-1">
                <SelectBox selected={selectedLeague} setSelected={setSelectedLeague} options={leagues} isMobile={onlyWidth < 768}/>
              </div>
              <div className="md:col-span-1">
                <SelectBox selected={selectedStatus} setSelected={setSelectedStatus} options={resultHubFilters.statuses} isMobile={onlyWidth < 768}/>
              </div>
            </div>
          </div>

          {isLoading ? <LoadingSpinner /> :
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {filteredMatches.map((match) => (
                <ResultCard 
                key={match.id} 
                league={match.league} 
                kickoff={match.kickoff} 
                fixtures={match.fixtures} 
                handleOpen={handleOpen} 
                snapshotDetails={match.snapshotDetails} 
                />
              ))}
              {filteredMatches.length === 0 && (
               <CustomNoData />
              )}
            </div>
          }
        </div>
      </div>
    </Fragment>
  );
};

export default ResultHub;
